import React, { useState } from 'react';
import { Redirect } from 'react-router-dom';

import {
    createOidcAuthenticationComponent,
    createAuthorization,
    createDecoratedRoute,
    useOidcAuthentication,
    Spinner,
    AuthErrorType,
} from '@cp-shared-19/frontend-ui';

import {
    dashboardPagePath,
    errorPagePath,
    landingPagePath,
    registrationPagePath,
    identificationPagePath,
} from '../components/navigation/paths';
import { getUserRegistryStatusEndpoint, UserRegistryStatus } from 'cpnl-common';
import { CpDataApi } from '../cp-xhr';
import { useTranslation } from 'react-i18next';
import { getAbsoluteUrlWithParams } from 'utils';

export function useLogin(pathFn: () => string = dashboardPagePath): () => Promise<void> {
    const { login } = useOidcAuthentication();
    const { i18n } = useTranslation();
    const locale = i18n.languages[0];
    const [isFirst, setIsFirst] = useState(true);

    if (isFirst) {
        setIsFirst(false);
        return (): Promise<void> =>
            login({
                redirect_uri: getAbsoluteUrlWithParams(pathFn()),
                redirectTarget: 'self',
                redirectMethod: 'assign',
                ui_locales: locale,
            });
    }
    return (): Promise<void> =>
        login({
            redirect_uri: getAbsoluteUrlWithParams(pathFn()),
            redirectTarget: 'self',
            redirectMethod: 'assign',
            ui_locales: locale,
        });
}

export const RedirectWithParams: React.FC<{ to: string }> = ({ to }) => {
    return (
        <Redirect
            to={{
                pathname: to,
                search: window.location.search,
            }}
        />
    );
};

export const getOidcErrorType = () => {
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('errorType') as AuthErrorType | null;
};

const getRedirectPathOnMissingAuthorization = (error?: boolean) => {
    const errorType = getOidcErrorType();
    if (errorType) return errorPagePath();
    if (error) return registrationPagePath();
    return identificationPagePath();
};

const Authentication = createOidcAuthenticationComponent({
    onMissingAuthentication: <RedirectWithParams to={landingPagePath()} />,
});

const commonAuthorizationOptions = {
    onLoading: <Spinner />,
    onError: <Redirect to={getRedirectPathOnMissingAuthorization(true)} />,
};

export const getErrorPagePathWithErrorType = (errorType: string) => errorPagePath() + `?errorType=${errorType}`;

const { Authorization: UserRegistrationAuthorization, useAuthorizationFlow: useRegistrationFlow } = createAuthorization(
    {
        displayName: 'UserRegistrationAuthorization',
        authorizationDataProvider: async () => {
            try {
                const { data: registrationData } = await CpDataApi.get<UserRegistryStatus>(
                    getUserRegistryStatusEndpoint(),
                );
                return {
                    isAuthorized: registrationData.isRegistered,
                };
            } catch (e) {
                return { isAuthorized: false };
            }
        },
        onMissingAuthorization: <Redirect to={getRedirectPathOnMissingAuthorization()} />,
        ...commonAuthorizationOptions,
    },
);

const AuthenticatedRoute = createDecoratedRoute('AuthenticatedRoute', Authentication);
const AuthorizedRoute = createDecoratedRoute('AuthorizedRoute', Authentication, UserRegistrationAuthorization);

export { AuthenticatedRoute, AuthorizedRoute, useRegistrationFlow };
